import { ColumnDef } from '@tanstack/react-table'
import { ArrowUpDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import type { PackageRow, PackageItemType } from '@/lib/api-client'
import { formatCurrency } from '@/lib/utils'

// Columns hidden by default (toggleable via the toolbar).
export const PACKAGE_HIDDEN_COLUMNS = ['branchName', 'packagePrice'] as const

const TYPE_STYLE: Record<PackageItemType, { bg: string; text: string; label: string }> = {
  treatment: { bg: '#3b82f6', text: 'white', label: 'Treatment' },
  consultation: { bg: '#525860', text: '#cbd5e1', label: 'Consult' },
  foc: { bg: '#10b981', text: 'white', label: 'FOC' },
}

function fmtDate(v: string | null): string {
  if (!v) return '-'
  // purchase_date is a DATE — take the date part, format DD Mon YYYY.
  const d = new Date(v)
  if (isNaN(d.getTime())) return v
  return d.toLocaleDateString('en-MY', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kuala_Lumpur' })
}

function SortHeader({ column, children }: { column: any; children: React.ReactNode }) {
  return (
    <Button
      variant="ghost"
      className="font-semibold px-0 hover:bg-transparent"
      onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
    >
      {children}
      <ArrowUpDown className="ml-2 h-4 w-4" />
    </Button>
  )
}

export function getPackageColumns(): ColumnDef<PackageRow>[] {
  return [
    {
      accessorKey: 'pno',
      header: 'PNO',
      cell: ({ row }) => <span className="font-mono text-xs text-text-muted">{row.original.pno}</span>,
      enableHiding: false,
    },
    {
      accessorKey: 'patientName',
      header: ({ column }) => <SortHeader column={column}>Patient</SortHeader>,
      cell: ({ row }) => (
        <div className="min-w-0">
          <div className="font-medium text-[13px] truncate max-w-[180px]">
            {row.original.patientName ?? <span className="italic text-text-muted">(unknown)</span>}
          </div>
          {row.original.phone && <div className="text-xs text-text-muted">{row.original.phone}</div>}
        </div>
      ),
      enableHiding: false,
    },
    {
      accessorKey: 'purchaseDate',
      header: ({ column }) => <SortHeader column={column}>Purchase</SortHeader>,
      cell: ({ row }) => <span className="text-[13px] whitespace-nowrap">{fmtDate(row.original.purchaseDate)}</span>,
    },
    {
      accessorKey: 'invoiceNo',
      header: 'Invoice',
      cell: ({ row }) => <span className="font-mono text-xs">{row.original.invoiceNo}</span>,
    },
    {
      accessorKey: 'packageName',
      header: ({ column }) => <SortHeader column={column}>Package</SortHeader>,
      cell: ({ row }) => (
        <span className="text-[13px] truncate max-w-[220px] inline-block align-bottom" title={row.original.packageName}>
          {row.original.packageName}
        </span>
      ),
    },
    {
      accessorKey: 'itemName',
      header: 'Item',
      cell: ({ row }) => {
        const isTreatment = row.original.itemType === 'treatment'
        return (
          <span className={`text-[13px] truncate max-w-[200px] inline-block align-bottom ${isTreatment ? 'font-medium text-text-dark' : 'text-text-muted'}`} title={row.original.itemName}>
            {row.original.itemName}
          </span>
        )
      },
    },
    {
      accessorKey: 'itemType',
      header: 'Type',
      cell: ({ row }) => {
        const s = TYPE_STYLE[row.original.itemType]
        return <Badge style={{ background: s.bg, color: s.text }} className="text-[10px]">{s.label}</Badge>
      },
    },
    {
      accessorKey: 'boughtQty',
      header: () => <div className="text-right">Bought</div>,
      cell: ({ row }) => <div className="text-right font-mono text-[13px]">{row.original.boughtQty}</div>,
    },
    {
      accessorKey: 'usedQty',
      header: () => <div className="text-right">Used</div>,
      cell: ({ row }) => <div className="text-right font-mono text-[13px] text-text-muted">{row.original.usedQty}</div>,
    },
    {
      accessorKey: 'balanceQty',
      header: ({ column }) => <div className="text-right"><SortHeader column={column}>Balance</SortHeader></div>,
      cell: ({ row }) => {
        const isTreatment = row.original.itemType === 'treatment'
        const bal = row.original.balanceQty
        return (
          <div className={`text-right font-mono text-[13px] font-semibold ${bal > 0 && isTreatment ? 'text-info' : bal > 0 ? 'text-text-dark' : 'text-text-muted'}`}>
            {bal}
          </div>
        )
      },
    },
    {
      accessorKey: 'packagePrice',
      header: () => <div className="text-right">Pkg Price</div>,
      cell: ({ row }) => (
        <div className="text-right text-[13px] text-text-muted">
          {row.original.packagePrice ? formatCurrency(row.original.packagePrice) : '-'}
        </div>
      ),
    },
    {
      accessorKey: 'branchName',
      header: 'Branch',
      cell: ({ row }) => <span className="text-xs text-text-muted">{row.original.branchName ?? '-'}</span>,
    },
  ]
}
